import React, { Suspense, useEffect } from 'react';

import { css } from '@emotion/css';
import { Theme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet } from '@tanstack/react-router';

import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles';
import { HandledErrorPage } from '@/pages/HandledErrorPage.tsx';
import { ValidateIdentity } from '@/pages/patients/ValidateIdentity.tsx';
import { Queries } from '@/queries/Queries.ts';
import { QueryAwait } from '@/queries/QueryAwait.tsx';
import { ScreenMax } from '@/theme/responsive.ts';
import { CenteredLoader } from '@/uiKit/atoms/pending/CenteredLoader.tsx';
import { ChatMessages } from '@/uiKit/organisms/ChatMessaging/ChatMessages.tsx';
import { ChatMessagingProvider } from '@/uiKit/organisms/ChatMessaging/ChatMessagingState.ts';
import { OpenMessagesFloatingButton } from '@/uiKit/organisms/ChatMessaging/OpenMessagesFloatingButton.tsx';
import {
  PATIENT_NAVIGATION_HEIGHT,
  PatientNavigationHeader,
  PatientNavigationHeaderSkeleton,
} from '@/uiKit/organisms/PatientNavigationHeader.tsx';

export const PractitionerManagePatientLayout: React.FC = () => {
  const styles = useStyles(makeStyles);
  const patientId = usePatientIdFromURL();
  const queryClient = useQueryClient();
  useEffect(() => {
    Queries.condition.useCondition.prefetch(queryClient, patientId);
    Queries.practitioner.usePatient.prefetch(queryClient, patientId);
  }, [queryClient, patientId]);

  return (
    <ChatMessagingProvider isOpen={false}>
      <Suspense fallback={<PatientNavigationHeaderSkeleton />}>
        <PatientNavigationHeader />
      </Suspense>
      <ValidateIdentity patientId={patientId} />
      <div className={styles.chatAside}>
        <div className={styles.content}>
          <section id="manage-patient-layout-main" className={styles.main}>
            <QueryAwait
              errorFallback={HandledErrorPage}
              pendingFallback={CenteredLoader}
            >
              <Outlet />
            </QueryAwait>
          </section>
        </div>
        <ChatMessages className={styles.chat} />
        <OpenMessagesFloatingButton className={styles.chatButton} />
      </div>
    </ChatMessagingProvider>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1',
    overflow: 'hidden',
  }),
  main: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(12)} 2vw;
  `,
  chatAside: css({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  }),
  chat: css({
    display: 'flex',
    height: `calc(100vh - ${PATIENT_NAVIGATION_HEIGHT})`,
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'sticky',
    top: PATIENT_NAVIGATION_HEIGHT,
    zIndex: 1,
    [ScreenMax.SM]: {
      width: '100% !important',
      height: `calc(100vh - ${PATIENT_NAVIGATION_HEIGHT} - ${theme.spacing(30)})`,
    },
  }),
  chatButton: css({
    position: 'sticky',
    top: PATIENT_NAVIGATION_HEIGHT,
  }),
});
